import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropdown", "selected", "search",
    "list", "hiddenField", "emptyState"
  ]

  static values = {
    professionId: String,
    endpoint: { type: String, default: "/occupations" }
  }

  connect() {
    this.occupations = []
    this.selectedId = this.professionIdValue || null
    this.searchTimeout = null
    this.debounceDelay = 200

    if (this.selectedId && this.hasSelectedTarget) {
      this.selectedTarget.classList.remove("text-gray-400")
      this.selectedTarget.classList.add("text-gray-900", "dark:text-white")
    }

    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.closeOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
  }

  closeOnOutsideClick(event) {
    if (this.hasDropdownTarget && !this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  openDropdown() {
    if (!this.hasDropdownTarget) return
    this.dropdownTarget.classList.remove("hidden")
    this.searchTarget.focus()
    this.fetchOccupations()
  }

  closeDropdown() {
    if (!this.hasDropdownTarget) return
    this.dropdownTarget.classList.add("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
    }
  }

  toggleDropdown(event) {
    event.stopPropagation()
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.openDropdown()
    } else {
      this.closeDropdown()
    }
  }

  search() {
    const query = this.searchTarget.value.trim()
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.fetchOccupations(query)
    }, this.debounceDelay)
  }

  async fetchOccupations(query = "") {
    try {
      const url = `${this.endpointValue}${query ? `?search=${encodeURIComponent(query)}` : ""}`
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error("Failed to fetch occupations")

      const q = typeof query === "string" ? query : ""
      this.occupations = await response.json()
      this.renderList(this.occupations, q)
    } catch (error) {
      console.error("[ProfessionSearch] Error fetching occupations:", error)
      this.occupations = []
      const q = typeof query === "string" ? query : ""
      this.renderList([], q)
    }
  }

  escapeAttr(text) {
    return String(text)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  /** Same rules as User#format_custom_profession_label: first character uppercased, rest lowercased (e.g. "chef d'entreprise" → "Chef d'entreprise"). */
  formatCustomProfessionLabel(query) {
    const s = (query || "").trim()
    if (s.length === 0) return ""
    const locale = document.documentElement.lang || "fr-FR"
    const first = s.charAt(0).toLocaleUpperCase(locale)
    const rest = s.slice(1).toLocaleLowerCase(locale)
    return first + rest
  }

  renderList(occupations, query = "") {
    if (!this.hasListTarget) return

    const trimmedQuery = (query || "").trim()

    if (occupations.length === 0) {
      if (trimmedQuery.length > 0) {
        if (this.hasEmptyStateTarget) {
          this.emptyStateTarget.classList.add("hidden")
        }
        this.listTarget.classList.remove("hidden")
        const formatted = this.formatCustomProfessionLabel(trimmedQuery)
        const idAttr = this.escapeAttr(formatted)
        const nameHtml = this.escapeHtml(formatted)
        const selected = formatted === this.selectedId
        this.listTarget.innerHTML = `
      <button type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors text-left ${selected ? "bg-sky-50 dark:bg-sky-900/20" : ""}"
              data-action="click->profession-search#selectOccupation"
              data-occupation-id="${idAttr}"
              data-occupation-name="${idAttr}">
        <span class="text-sm text-gray-900 dark:text-white">${nameHtml}</span>
        ${selected ? '<svg class="w-4 h-4 ml-auto text-sky-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>' : ""}
      </button>
    `
        return
      }
      this.listTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
    this.listTarget.classList.remove("hidden")

    this.listTarget.innerHTML = occupations.map(occupation => {
      const idAttr = this.escapeAttr(occupation.id)
      const nameAttr = this.escapeAttr(occupation.name)
      const nameHtml = this.escapeHtml(occupation.name)
      return `
      <button type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors text-left ${occupation.id === this.selectedId ? "bg-sky-50 dark:bg-sky-900/20" : ""}"
              data-action="click->profession-search#selectOccupation"
              data-occupation-id="${idAttr}"
              data-occupation-name="${nameAttr}">
        <span class="text-sm text-gray-900 dark:text-white">${nameHtml}</span>
        ${occupation.id === this.selectedId ? '<svg class="w-4 h-4 ml-auto text-sky-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>' : ""}
      </button>
    `
    }).join("")
  }

  selectOccupation(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.occupationId
    const name = event.currentTarget.dataset.occupationName

    this.selectedId = id
    this.hiddenFieldTarget.value = id
    this.selectedTarget.textContent = name
    this.selectedTarget.classList.remove("text-gray-400")
    this.selectedTarget.classList.add("text-gray-900", "dark:text-white")

    this.closeDropdown()
  }
}
